import { isBlockSolid } from "../world/block-registry";
import type { WorldBlockAccess } from "./collision";

export type RaycastHit = {
  blockX: number;
  blockY: number;
  blockZ: number;
  faceNormal: [number, number, number];
  adjacentX: number;
  adjacentY: number;
  adjacentZ: number;
  distance: number;
};

export function raycastVoxels(
  originX: number,
  originY: number,
  originZ: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  maxDistance: number,
  world: WorldBlockAccess
): RaycastHit | null {
  let mapX = Math.floor(originX);
  let mapY = Math.floor(originY);
  let mapZ = Math.floor(originZ);

  const stepX = dirX > 0 ? 1 : dirX < 0 ? -1 : 0;
  const stepY = dirY > 0 ? 1 : dirY < 0 ? -1 : 0;
  const stepZ = dirZ > 0 ? 1 : dirZ < 0 ? -1 : 0;

  const tDeltaX = dirX !== 0 ? Math.abs(1 / dirX) : Infinity;
  const tDeltaY = dirY !== 0 ? Math.abs(1 / dirY) : Infinity;
  const tDeltaZ = dirZ !== 0 ? Math.abs(1 / dirZ) : Infinity;

  let tMaxX: number;
  let tMaxY: number;
  let tMaxZ: number;

  if (dirX > 0) {
    tMaxX = (mapX + 1 - originX) * tDeltaX;
  } else if (dirX < 0) {
    tMaxX = (originX - mapX) * tDeltaX;
  } else {
    tMaxX = Infinity;
  }

  if (dirY > 0) {
    tMaxY = (mapY + 1 - originY) * tDeltaY;
  } else if (dirY < 0) {
    tMaxY = (originY - mapY) * tDeltaY;
  } else {
    tMaxY = Infinity;
  }

  if (dirZ > 0) {
    tMaxZ = (mapZ + 1 - originZ) * tDeltaZ;
  } else if (dirZ < 0) {
    tMaxZ = (originZ - mapZ) * tDeltaZ;
  } else {
    tMaxZ = Infinity;
  }

  let face: [number, number, number] = [0, 0, 0];
  let t = 0;

  const maxSteps = Math.ceil(maxDistance * 3);

  for (let i = 0; i < maxSteps; i++) {
    if (tMaxX < tMaxY) {
      if (tMaxX < tMaxZ) {
        t = tMaxX;
        if (t > maxDistance) return null;
        mapX += stepX;
        face = [stepX > 0 ? -1 : 1, 0, 0];
        tMaxX += tDeltaX;
      } else {
        t = tMaxZ;
        if (t > maxDistance) return null;
        mapZ += stepZ;
        face = [0, 0, stepZ > 0 ? -1 : 1];
        tMaxZ += tDeltaZ;
      }
    } else {
      if (tMaxY < tMaxZ) {
        t = tMaxY;
        if (t > maxDistance) return null;
        mapY += stepY;
        face = [0, stepY > 0 ? -1 : 1, 0];
        tMaxY += tDeltaY;
      } else {
        t = tMaxZ;
        if (t > maxDistance) return null;
        mapZ += stepZ;
        face = [0, 0, stepZ > 0 ? -1 : 1];
        tMaxZ += tDeltaZ;
      }
    }

    if (isBlockSolid(world.getBlockAtWorld(mapX, mapY, mapZ))) {
      return {
        blockX: mapX,
        blockY: mapY,
        blockZ: mapZ,
        faceNormal: face,
        adjacentX: mapX + face[0],
        adjacentY: mapY + face[1],
        adjacentZ: mapZ + face[2],
        distance: t,
      };
    }
  }

  return null;
}

export function getCameraDirection(yaw: number, pitch: number) {
  return {
    x: -Math.sin(yaw) * Math.cos(pitch),
    y: Math.sin(pitch),
    z: -Math.cos(yaw) * Math.cos(pitch),
  };
}
